import { z } from "zod";
import { ProductFormType } from "src/pages/Product/ProductForm/ProductForm.types";
import { WizardStepKey } from "./useWizardStepsV2";
import { getRecipeWeightValidation, isValidRecipeQuantity } from "./recipeWeightValidation";

/**
 * Translation key for the inline error attached to each field. Mapped to the section
 * that visually owns the field — see `validateProductForm` below.
 */
export type FieldError = {
    field: string;
    messageKey: string;
    message?: string;
};

export type SectionErrors = Partial<Record<WizardStepKey, FieldError[]>>;

export type ValidateProductFormResult =
    | { success: true; errorsBySection: SectionErrors }
    | { success: false; errorsBySection: SectionErrors };

/**
 * Aggregated Zod schema for the long-scroll V2 product form (Figma `12819:67393`).
 *
 * Mirrors the per-step Zod schemas that the V2 step components were carrying:
 *  - identity: title (min 1), clientId (min 1), netWeight (> 0)
 *  - organization: ownedBy (object with `id`); productLine is optional.
 *
 * `productLine` is no longer required — the API accepts a null `productLine`, and
 * the V2 form may hide the Organization section entirely when the active company
 * has no product lines, brands, or sub-companies (see `useOrganizationSectionVisibility`).
 *
 * `ownedBy` is auto-filled with the active company at form-init time in
 * `ProductFormV2.tsx`, so the nullable shape is just a defensive fallback.
 *
 * Optional fields (productReference, brand, tags, ssq_id) stay optional to match the
 * v1 wizard surface — the user is free to save without them.
 */
const productFormSchema = z.object({
    title: z.string().trim().min(1),
    ssq_id: z.number().nullable().optional(),
    clientId: z.string().trim().min(1),
    netWeight: z.number().gt(0),
    ownedBy: z
        .object({
            "@id": z.string(),
            id: z.number(),
            name: z.string(),
        })
        .nullable(),
});

/**
 * Maps each top-level Zod path to the WizardStepKey of the section that visually owns
 * the field.
 */
const fieldToSection: Record<string, WizardStepKey> = {
    title: "identity",
    ssq_id: "identity",
    clientId: "identity",
    netWeight: "identity",
    productReference: "identity",
    ownedBy: "organization",
    brand: "organization",
};

const isPositive = (value: number | null | undefined): boolean => typeof value === "number" && value > 0;

/**
 * Validates the product form state and groups the resulting errors per section so the
 * V2 long-scroll page can:
 *  1. block the Save action if anything is invalid,
 *  2. summarize the error types in the focused validation banner,
 *  3. display field-level error messages inline within each section.
 *
 * Per-step `useForm` instances still drive on-change validation feedback inside each
 * section. This function is the global gate at Save time.
 */
export const validateProductForm = (
    product: ProductFormType,
    options: { validateRecipe?: boolean } = {},
): ValidateProductFormResult => {
    const errorsBySection: SectionErrors = {};

    const pushError = (section: WizardStepKey, error: FieldError) => {
        if (!errorsBySection[section]) {
            errorsBySection[section] = [];
        }
        errorsBySection[section]!.push(error);
    };

    // Required-object constraint handled outside Zod so we can keep an expressive
    // object shape (vs. just .nullable()) without forcing the Zod parse to fail on
    // cascading invalid_type errors when title/clientId already failed.
    if (product.ownedBy == null && product.shares.length === 0) {
        pushError("organization", { field: "ownedBy", messageKey: "form.required_field" });
    }

    // Zod-driven scalar checks. We feed a nullable() shape for ownedBy so the parse
    // doesn't crash on the absence case handled above.
    const result = productFormSchema.safeParse({
        title: product.title ?? "",
        ssq_id: product.ssq_id,
        clientId: product.clientId ?? "",
        netWeight: product.netWeight ?? 0,
        ownedBy: product.ownedBy ?? null,
    });

    if (!result.success) {
        for (const issue of result.error.issues) {
            const field = String(issue.path[0] ?? "");
            const section = fieldToSection[field];
            if (!section) continue;
            pushError(section, { field, messageKey: issueToMessageKey(issue, field) });
        }
    }

    if (options.validateRecipe !== false) {
        product.productIngredients.forEach((ingredient, index) => {
            if (!isValidRecipeQuantity(ingredient.quantity)) {
                pushError("recipe", {
                    field: `productIngredients.${index}.quantity`,
                    messageKey: "form.required_field",
                });
            }
        });

        if (getRecipeWeightValidation(product).hasWeightMismatch) {
            pushError("recipe", {
                field: "recipeWeight",
                messageKey: "product.v2.recipe.net_weight_mismatch",
            });
        }
    }

    product.productPackagings.forEach((productPackaging, index) => {
        const fieldPrefix = `productPackagings.${index}`;

        if (productPackaging.packagingReference?.kind === "element" && !isPositive(productPackaging.usedWeight)) {
            pushError("packaging", {
                field: `${fieldPrefix}.usedWeight`,
                messageKey: "product_packaging.element.used_weight_required",
            });
        }

        if (productPackaging.packagingReference?.kind === "composite" && !isPositive(productPackaging.quantity)) {
            pushError("packaging", {
                field: `${fieldPrefix}.quantity`,
                messageKey: "product_packaging.composite.quantity_required",
            });
        }

        if (productPackaging.packaging && !isPositive(productPackaging.quantity)) {
            pushError("packaging", {
                field: `${fieldPrefix}.quantity`,
                messageKey: "product_packaging.packaging.quantity_required",
            });
        }

        if (!productPackaging.packaging && !productPackaging.packagingReference) {
            pushError("packaging", {
                field: fieldPrefix,
                messageKey: "product_packaging.target.required",
            });
        }
    });

    const hasErrors = Object.keys(errorsBySection).length > 0;
    return hasErrors ? { success: false, errorsBySection } : { success: true, errorsBySection: {} };
};

/**
 * Maps a Zod issue to a translation key. We distinguish:
 *  - string/array `too_small` (e.g. `title: ""`) → required_field
 *  - number `too_small` (e.g. `netWeight: 0`) → invalid_field (the field IS set,
 *    just with an invalid value)
 *  - invalid_type → required_field (the user didn't provide a value)
 *
 * `origin` is the runtime field zod uses to carry the parsed-from primitive type;
 * see the IssueData union in zod (and its accepting `as { origin?: string }` cast
 * lets us read it without depending on a specific minor-version type).
 */
const issueToMessageKey = (issue: z.ZodIssue, _field: string): string => {
    if (issue.code === "invalid_type") return "form.required_field";
    if (issue.code === "too_small") {
        const origin = (issue as { origin?: string }).origin;
        if (origin === "string" || origin === "array") return "form.required_field";
        return "form.invalid_field";
    }
    return "form.invalid_field";
};
